import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	InvoiceApi,
	InvoiceCalculationType,
	InvoiceDetailsApi,
	InvoiceOrderApi,
	TaxApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import InvoiceFormBasicInfo from "./InvoiceFormBasicInfo";
import InvoiceFormClients from "./InvoiceFormClients";
import InvoiceFormItemsTable from "./Items/InvoiceFormItemsTable";

interface Props {
	invoice?: InvoiceApi;
	customFieldsConfig: CustomFieldTemplateApi[];
	invoiceDetails?: InvoiceDetailsApi;
	withoutItems?: boolean;
	calculationType?: InvoiceCalculationType;
	orders?: InvoiceOrderApi[];
}

const InvoiceForm = ({ invoice, customFieldsConfig, invoiceDetails, withoutItems, calculationType, orders }: Props) => {
	const form = useForm<InvoiceApi>({
		defaultValues: {
			...invoice,
			dates: {
				...invoice?.dates,
				issue_date: invoice?.dates?.issue_date ?? formatDateToString(new Date()),
			},
			items: withoutItems ? [] : invoice?.items ? [...invoice.items, { name: "" }] : [{ name: "" }],
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], invoice?.custom_fields || []),
			calculation_type: calculationType ?? invoice?.calculation_type ?? InvoiceCalculationType.GROSS,
			place_of_issue: invoice ? invoice.place_of_issue : invoiceDetails?.invoice_place,
			issuer_person_name: invoice ? invoice.issuer_person_name : invoiceDetails?.issuer_name,
			orders: invoice?.copied ? [] : invoice?.orders || [],
		},
		criteriaMode: "all",
	});
	const { formState, handleSubmit, setError } = form;
	const [loading, setLoading] = useState(false);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [taxes, setTaxes] = useState<TaxApi[] | undefined>();
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	useEffect(() => {
		fetchTaxes();
	}, []);

	const fetchTaxes = async () => {
		try {
			const res = await apiOrganization.getTaxes();
			setTaxes(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const getParsedDataForm = (data: InvoiceApi) => {
		data.items = [
			...data.items.filter(
				(item) =>
					item?.volume ||
					item?.name ||
					item?.quantity ||
					item?.tax_id ||
					item?.price?.amount ||
					item?.measure_type ||
					item?.pkwiu ||
					item?.gtu
			),
		];
		data.custom_fields = data.custom_fields.filter(
			(customField) => customField.value !== undefined && customField.value !== null && customField.value !== ""
		);
		if (orders) {
			data.orders = orders;
		}

		return data;
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const parsedData = getParsedDataForm(data);
			let res: InvoiceApi;
			if (invoice?.id && !invoice?.copied) {
				res = await apiOrganization.updateInvoice(invoice.id, parsedData);
			} else {
				res = await apiOrganization.createInvoice(parsedData);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
			history.push(`/${organization.id}/documents/invoices/${res.id}`);
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	if (!taxes) return <LoadingContainer />;

	return (
		<FormDirty
			formState={formState}
			onSubmit={(event) => {
				onSubmit(event);
			}}
			loading={loading}
		>
			<InvoiceFormBasicInfo form={form} />
			<InvoiceFormClients form={form} setErrors={setErrors} />
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={invoice?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
			{!withoutItems && <InvoiceFormItemsTable form={form} taxes={taxes} />}
		</FormDirty>
	);
};

export default InvoiceForm;
